import React, { useState } from "react";
import "./AnalyticsHeroPlatformLogo.css";
import { PLATFORM_HERO_IMAGES, resolvePlatformHeroKey } from "../utils/platformBranding";

/**
 * The platform mark at the front of an individual platform's Analytics hero
 * pill: LOGO → NAME → ANALYTICS badge. Byte-identical in Cortex and Quantum.
 *
 *  1. ONE ASSET MAPPING. Artwork comes from the canonical Platform Analytics
 *     hero contract (`resolvePlatformHeroKey` + `PLATFORM_HERO_IMAGES`), so the
 *     hero can never show a different file from the one every other platform
 *     surface shows.
 *
 *  2. NO WRONG MARK. A key the contract does not know renders nothing. An
 *     aggregate hero (Omni, Platform Analytics) is not a platform, and the
 *     `unknown` placeholder artwork is not a logo. A file that fails at runtime
 *     also removes itself rather than leaving a broken image in the pill.
 *
 *  3. ONE SIZE. The frame is a fixed square (a wider slot only for Viant, whose
 *     only artwork is a wordmark), so every platform's mark sits at the same
 *     size and baseline before the image decodes. Marks drawn inside generous
 *     black padding are scaled uniformly within the frame by CSS; the pill is
 *     black, so only padding is trimmed and proportions are never distorted.
 *
 * Decorative: the platform name is real text immediately beside it, so the
 * frame is `aria-hidden` and the image has `alt=""` — a screen reader announces
 * "Yahoo Analytics", never "Yahoo Yahoo Analytics".
 */
export default function AnalyticsHeroPlatformLogo({ platformKey }) {
  // The failure belongs to the artwork that failed, not to the component: an
  // instance survives client-side navigation between routes of the same page
  // type (/viant-analytics → /stackadapt-analytics), and the next platform's
  // valid artwork must still render.
  const [failedSrc, setFailedSrc] = useState(null);
  const heroKey = resolvePlatformHeroKey(platformKey);
  const src = heroKey === "unknown" ? null : PLATFORM_HERO_IMAGES[heroKey];

  if (!src || failedSrc === src) return null;

  return (
    <span
      className={`cphere-analytics-hero-logo cphere-analytics-hero-logo--${heroKey}`}
      data-platform-hero={heroKey}
      aria-hidden="true"
    >
      <img
        className="cphere-analytics-hero-logo-image"
        src={src}
        alt=""
        decoding="async"
        draggable="false"
        onError={() => setFailedSrc(src)}
      />
    </span>
  );
}
